import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Clock } from "lucide-react";
import SEO, { SITE_URL } from "../components/SEO";
import { PageHero } from "./Services";
import { heroImage } from "../data/site";
import { sortedPosts, categories, categoryName } from "../data/blog";

const fmt = (iso) =>
  new Date(iso + "T12:00:00Z").toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });

const jsonLd = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "Blog",
      "@id": `${SITE_URL}/resources#blog`,
      name: "Pepla Roofing Resources",
      description:
        "Straight answers about roofs in Phoenix and Maricopa County — tile underlayment, monsoon damage, ventilation, cost and hiring a contractor.",
      url: `${SITE_URL}/resources`,
      publisher: { "@type": "RoofingContractor", name: "Pepla Roofing", url: SITE_URL },
      blogPost: sortedPosts.map((p) => ({
        "@type": "BlogPosting",
        headline: p.title,
        description: p.description,
        datePublished: p.date,
        url: `${SITE_URL}/resources/${p.slug}`,
      })),
    },
    {
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
        { "@type": "ListItem", position: 2, name: "Resources", item: `${SITE_URL}/resources` },
      ],
    },
  ],
};

const Blog = () => {
  const [cat, setCat] = useState("all");
  const shown = cat === "all" ? sortedPosts : sortedPosts.filter((p) => p.category === cat);
  const [lead, ...rest] = shown;

  return (
    <div>
      <SEO
        title="Roofing Resources & Guides | Phoenix, AZ | Pepla Roofing"
        description="Straight answers about Phoenix roofs: why tile roofs leak when the tile looks fine, what attic ventilation code actually requires in Arizona, what a roof costs, and what to do after a monsoon."
        jsonLd={jsonLd}
      />

      <PageHero
        eyebrow="Resources"
        title="Straight Answers About Roofs"
        subtitle="Written for homeowners in the Valley, by the people who do the work. No gated PDFs, no sales funnel."
        image={heroImage}
      />

      <section className="py-20 bg-background">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="flex flex-wrap gap-2 mb-12" role="group" aria-label="Filter articles by topic">
            {[{ key: "all", name: "Everything" }, ...categories].map((c) => {
              const on = cat === c.key;
              return (
                <button
                  key={c.key}
                  type="button"
                  onClick={() => setCat(c.key)}
                  aria-pressed={on}
                  className={`text-xs rounded-full px-4 py-2 border transition-colors focus:outline-none focus:ring-2 focus:ring-brand ${
                    on ? "bg-ink text-white border-ink" : "border-ink/15 text-ink/65 hover:border-brand hover:text-brand"
                  }`}
                >
                  {c.name}
                </button>
              );
            })}
          </div>

          {lead && (
            <Link
              to={`/resources/${lead.slug}`}
              className="group block bg-card border border-ink/10 rounded-2xl p-8 md:p-10 mb-8 card-lift"
            >
              <span className="kicker">{categoryName(lead.category)}</span>
              <h2 className="font-heading text-3xl md:text-4xl uppercase tracking-tight mt-3 mb-4 group-hover:text-brand transition-colors">
                {lead.title}
              </h2>
              <div className="brand-line mb-5" />
              <p className="text-muted-foreground text-lg max-w-3xl mb-6">{lead.description}</p>
              <span className="flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-ink/45">
                <span>{fmt(lead.date)}</span>
                <span className="inline-flex items-center gap-1.5">
                  <Clock size={13} aria-hidden="true" /> {lead.read} min read
                </span>
                <span className="inline-flex items-center gap-1.5 text-brand-dark font-medium">
                  Read it <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                </span>
              </span>
            </Link>
          )}

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {rest.map((p) => (
              <Link
                key={p.slug}
                to={`/resources/${p.slug}`}
                className="group card-lift bg-card border border-ink/10 rounded-2xl p-7 flex flex-col"
              >
                <span className="kicker">{categoryName(p.category)}</span>
                <h3 className="font-heading text-xl uppercase tracking-wide mt-3 mb-3 group-hover:text-brand transition-colors">
                  {p.title}
                </h3>
                <p className="text-muted-foreground text-sm leading-relaxed flex-1">{p.description}</p>
                <span className="flex items-center gap-4 text-xs text-ink/45 mt-5">
                  <span>{fmt(p.date)}</span>
                  <span className="inline-flex items-center gap-1.5">
                    <Clock size={13} aria-hidden="true" /> {p.read} min
                  </span>
                </span>
              </Link>
            ))}
          </div>

          {shown.length === 0 && (
            <p className="text-muted-foreground">Nothing filed under that topic yet.</p>
          )}
        </div>
      </section>

      <section className="py-20 section-dark text-white text-center">
        <div className="max-w-3xl mx-auto px-5 lg:px-8">
          <h2 className="font-heading text-4xl md:text-5xl uppercase tracking-tight mb-4">
            Want It Looked At Properly?
          </h2>
          <p className="text-white/70 mb-8">
            Inspections and written scopes are free, seven days a week. You get the photographs and the scope
            whether or not you hire us.
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 bg-brand hover:bg-brand-light text-ink font-semibold uppercase tracking-wider text-sm px-8 py-4 rounded-md transition-colors"
          >
            Book A Free Inspection <ArrowRight size={18} />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Blog;
